import { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Control, Controller, useWatch } from "react-hook-form";
import { DateTimePicker } from "../../components/common/DateTimePicker/DateTimePicker";
import Typeahead from "../../components/common/Typeahead/Typeahead";
import Select from "../../components/common/Select/Select";
import MultiSelect from "../../components/common/MultiSelect/MultiSelect";
import { useInteractionTags } from "../../hooks/useInteraction";
import { InteractionType } from "../../types/interaction/Interaction";

interface WidgetFilterSectionProps {
  control: Control<any>;
  customerId: string;
}

export default function WidgetFilterSection({
  control,
  customerId,
}: Readonly<WidgetFilterSectionProps>) {
  const [open, setOpen] = useState(false);
  const { data: tags = [] } = useInteractionTags(customerId);

  // Count active filters for the summary badge
  const filterValues = useWatch({
    control,
    name: [
      "filterFrom",
      "filterTo",
      "filterAgentName",
      "filterDirection",
      "filterInteractionType",
      "filterSkill",
      "filterGroup",
      "filterTags",
    ],
  });

  const activeFilterCount = filterValues.filter((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v != null && v !== "";
  }).length;

  return (
    <Box>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ px: 0 }}>
        <ListItemText
          primary={
            <Typography variant="overline" color="text.secondary">
              Filters
              {activeFilterCount > 0 && ` (${activeFilterCount} active)`}
            </Typography>
          }
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DateTimePicker
            name="filterFrom"
            label="From"
            control={control}
          />
          <DateTimePicker
            name="filterTo"
            label="To"
            control={control}
          />
          <Controller
            name="filterAgentName"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Typeahead
                {...field}
                label="Agent Name"
                pascalFieldName="AgentName"
                userInputValue={field.value ?? ""}
                customerId={customerId}
                freeSolo
                fullWidth
              />
            )}
          />
          <Select
            size="small"
            name="filterDirection"
            control={control}
            fullWidth
            label="Direction"
            options={["in", "out"]}
          />
          <Select
            size="small"
            name="filterInteractionType"
            control={control}
            fullWidth
            label="Interaction Type"
            options={Object.values(InteractionType)}
          />
          <Controller
            name="filterSkill"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Typeahead
                {...field}
                label="Skill"
                pascalFieldName="Skill"
                userInputValue={field.value ?? ""}
                customerId={customerId}
                freeSolo
                fullWidth
              />
            )}
          />
          <Controller
            name="filterGroup"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Typeahead
                {...field}
                label="Group"
                pascalFieldName="Group"
                userInputValue={field.value ?? ""}
                customerId={customerId}
                freeSolo
                fullWidth
              />
            )}
          />
          <MultiSelect
            fullWidth
            name="filterTags"
            label="Tags"
            control={control}
            options={tags}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            limitTags={5}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
